/**
 * Text written outside Curated Planet's own words -- a company's AI vocabulary (terms, other words, definitions), a
 * requester's reason -- shown as received. The same approach as the console's `components/UntrustedText.tsx` (client
 * MCP FE-03; backend `content_is_untrusted_data`):
 *
 * - **plain text**: a React text child (line breaks kept), never markup, never Markdown, never auto-linked -- an
 *   `<img>`, a `<script>` or `[a](javascript:…)` reads as the characters it is (no `dangerouslySetInnerHTML`);
 * - **labelled**: the block says whose words they are, so nobody reads them as ours;
 * - **kept apart** from our own fields (names, statuses, actions), which stay outside the quoted values.
 *
 * `UntrustedBlock` is the labelled frame (one per list or payload, with a legend); `UntrustedText` one value inside it
 * (`inline` for a value in a line of our own words, e.g. a table cell).
 */
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { Box, Stack, Typography } from '@mui/material';
import * as React from 'react';

export const UntrustedBlock = ({ title, legend, children, sx }) => (
    <Box
        component="section"
        aria-label={`${title} (untrusted text)`}
        data-untrusted-block
        sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, bgcolor: 'background.default', ...sx }}
    >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <ShieldOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} aria-hidden />
            <Typography variant="subtitle2">{title}</Typography>
        </Stack>
        {legend && (
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0, mb: 1.5 }}>
                {legend}
            </Typography>
        )}
        {children}
    </Box>
);

const QUOTED = {
    m: 0,
    pl: 1.5,
    py: 0.5,
    borderLeft: 3,
    borderColor: 'divider',
    bgcolor: 'background.paper',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontSize: 13,
    lineHeight: 1.5,
};

/** One value: plain text, line breaks kept, long words broken. Empty text says so. */
export const UntrustedText = ({ text, label, empty = 'Nothing written.', inline = false }) => {
    const has = text !== null && text !== undefined && String(text) !== '';
    if (inline) {
        return has ? (
            <Box component="span" data-untrusted-text sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {String(text)}
            </Box>
        ) : (
            <Box component="span" sx={{ color: 'text.disabled' }}>{empty}</Box>
        );
    }
    return (
        <Box>
            {label && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                    {label}
                </Typography>
            )}
            {has ? (
                <Box component="blockquote" data-untrusted-text sx={QUOTED}>
                    {String(text)}
                </Box>
            ) : (
                <Typography variant="body2" color="text.disabled">
                    {empty}
                </Typography>
            )}
        </Box>
    );
};
